import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Compass, Upload, CheckCircle, Camera, Shield, Building, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const uploadFile = async (base64Data, fileType = 'document') => {
  const res = await axios.post(`${API}/pilot-registrations/upload-file`, {
    file_data: base64Data,
    file_type: fileType
  });
  return res.data.file_id;
};

const FileUploadField = ({ label, required, value, onChange, accept = '.pdf,.jpg,.jpeg,.png', hint }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; }
    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const fileId = await uploadFile(base64, accept.includes('image') ? 'photo' : 'document');
      onChange(fileId, base64);
    } catch (err) {
      toast.error('Upload failed. Please try again.');
    } finally { setUploading(false); }
  };
  return (
    <div>
      <Label className="text-sm">{label} {required && <span className="text-red-500">*</span>}</Label>
      {hint && <p className="text-xs text-gray-400 mb-1">{hint}</p>}
      <div className="flex items-center gap-2 mt-1">
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} className="text-xs">
          <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? 'Uploading...' : value ? 'Replace' : 'Upload'}
        </Button>
        {value && <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> Uploaded</span>}
        <input ref={inputRef} type="file" accept={accept} onChange={handleFile} className="hidden" />
      </div>
    </div>
  );
};

const PilotRegistration = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1: Details, 2: Insurance, 3: Photos
  const [form, setForm] = useState({
    name: '', address: '', abn: '', phone: '', company_name: '', email: '',
    public_liability: null, vehicle_insurance: null, pilot_insurance: null,
    steerer_insurance: null, workers_comp: null, pilot_accreditations: null,
    msic: null, port_passes: null,
    accreditation_codes: [],
    vehicle_photo_front: null, vehicle_photo_rear: null
  });
  const [photoPreviews, setPhotoPreviews] = useState({ front: null, rear: null });

  const set = (field) => (val, preview) => {
    if (typeof val === 'object' && val?.target) {
      setForm(p => ({ ...p, [field]: val.target.value }));
    } else {
      setForm(p => ({ ...p, [field]: val }));
      if (field === 'vehicle_photo_front' && preview) setPhotoPreviews(p => ({ ...p, front: preview }));
      if (field === 'vehicle_photo_rear' && preview) setPhotoPreviews(p => ({ ...p, rear: preview }));
    }
  };

  const validateStep1 = () => {
    const required = ['name', 'address', 'abn', 'phone', 'company_name', 'email'];
    const missing = required.filter(f => !form[f]);
    if (missing.length) { toast.error(`Please fill in: ${missing.join(', ').replace(/_/g, ' ')}`); return false; }
    if (!/\S+@\S+\.\S+/.test(form.email)) { toast.error('Please enter a valid email address'); return false; }
    return true;
  };

  const validateStep2 = () => {
    if (!form.pilot_insurance) { toast.error('Pilot Insurance is required'); return false; }
    return true;
  };

  const validateStep3 = () => {
    if (!form.vehicle_photo_front || !form.vehicle_photo_rear) {
      toast.error('Both front and rear vehicle photos are required'); return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/pilot-registrations`, form);
      toast.success('Registration submitted! You will be notified once approved.');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit registration');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-[#1e3a5f] text-white py-10">
        <div className="container mx-auto px-4 text-center">
          <Compass className="w-10 h-10 mx-auto mb-3 text-red-400" />
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Pilot / Steerer Registration</h1>
          <p className="text-blue-200">Register as a pilot or steerer to work with Truckn Send It</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Progress */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-3">
            {[
              { num: 1, label: 'Details' },
              { num: 2, label: 'Insurance' },
              { num: 3, label: 'Vehicle Photos' }
            ].map((s, i) => (
              <React.Fragment key={s.num}>
                {i > 0 && <div className="w-10 h-0.5 bg-gray-300" />}
                <div className={`flex items-center ${step >= s.num ? 'text-[#1e3a5f]' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= s.num ? 'bg-[#1e3a5f] text-white' : 'bg-gray-300'}`}>{s.num}</div>
                  <span className="ml-1.5 text-sm font-medium hidden sm:inline">{s.label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step 1: Personal & Business Details */}
        {step === 1 && (
          <Card className="p-6">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-1 flex items-center gap-2"><Building className="w-5 h-5" /> Personal & Business Details</h2>
            <p className="text-sm text-gray-500 mb-5">All fields are required</p>
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Full Name <span className="text-red-500">*</span></Label><Input value={form.name} onChange={set('name')} placeholder="John Smith" data-testid="pilot-name" /></div>
                <div><Label>Company Name <span className="text-red-500">*</span></Label><Input value={form.company_name} onChange={set('company_name')} placeholder="Smith Pilots Pty Ltd" data-testid="pilot-company" /></div>
              </div>
              <div><Label>Address <span className="text-red-500">*</span></Label><Input value={form.address} onChange={set('address')} placeholder="123 Main St, Adelaide SA 5000" data-testid="pilot-address" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>ABN <span className="text-red-500">*</span></Label><Input value={form.abn} onChange={set('abn')} placeholder="12 345 678 901" data-testid="pilot-abn" /></div>
                <div><Label>Phone Number <span className="text-red-500">*</span></Label><Input value={form.phone} onChange={set('phone')} placeholder="0400 123 456" data-testid="pilot-phone" /></div>
              </div>
              <div><Label>Email Address <span className="text-red-500">*</span></Label><Input type="email" value={form.email} onChange={set('email')} placeholder="john@smithpilots.com.au" data-testid="pilot-email" /></div>

              <Button onClick={() => { if (validateStep1()) setStep(2); }} className="w-full bg-[#1e3a5f] hover:bg-[#15304f] text-white mt-4" data-testid="pilot-step1-next">
                Continue to Insurance Documents
              </Button>
            </div>
          </Card>
        )}

        {/* Step 2: Insurance Documents */}
        {step === 2 && (
          <Card className="p-6">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-1 flex items-center gap-2"><Shield className="w-5 h-5" /> Insurance & Accreditations</h2>
            <p className="text-sm text-gray-500 mb-5">Upload your insurance documents. Fields marked * are mandatory.</p>
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-red-800 mb-3">Mandatory</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <FileUploadField label="Pilot Insurance" required value={form.pilot_insurance} onChange={set('pilot_insurance')} />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-blue-800 mb-3">Optional Documents</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <FileUploadField label="Public Liability" value={form.public_liability} onChange={set('public_liability')} />
                  <FileUploadField label="Vehicle Insurance" value={form.vehicle_insurance} onChange={set('vehicle_insurance')} />
                  <FileUploadField label="Workers Comprehensive" value={form.workers_comp} onChange={set('workers_comp')} hint="If applicable" />
                  <FileUploadField label="Steerer Insurance" value={form.steerer_insurance} onChange={set('steerer_insurance')} />
                  <FileUploadField label="Pilot Accreditation(s)" value={form.pilot_accreditations} onChange={set('pilot_accreditations')} hint="Upload certificate/s" />
                  <FileUploadField label="MSIC" value={form.msic} onChange={set('msic')} hint="Maritime Security ID Card" />
                  <FileUploadField label="Port Passes" value={form.port_passes} onChange={set('port_passes')} />
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100">
                  <Label>Accreditation types <span className="text-red-500">*</span></Label>
                  <p className="text-xs text-gray-500 mb-2">Tick every accreditation you hold — shippers filter by these on the pilots map.</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="pilot-signup-accreditations">
                    {['VIC LVL1','VIC LVL2','VIC TC','QLD LVL1','QLD LVL2','WA','VIC PROB','NT PILOT','NT ESCORT','4WD SPECIFIC','DIESEL','PETROL','GAS'].map((a) => {
                      const active = (form.accreditation_codes || []).includes(a);
                      return (
                        <button
                          type="button"
                          key={a}
                          onClick={() => setForm((p) => ({
                            ...p,
                            accreditation_codes: active
                              ? (p.accreditation_codes || []).filter((x) => x !== a)
                              : [...(p.accreditation_codes || []), a],
                          }))}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                            active ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#1e3a5f]'
                          }`}
                          data-testid={`pilot-signup-accreditation-${a.replace(/\s+/g, '-')}`}
                        >
                          {a}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">Back</Button>
                <Button onClick={() => { if (validateStep2()) setStep(3); }} className="flex-1 bg-[#1e3a5f] hover:bg-[#15304f] text-white" data-testid="pilot-step2-next">
                  Continue to Vehicle Photos
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Vehicle Photos */}
        {step === 3 && (
          <Card className="p-6">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-1 flex items-center gap-2"><Camera className="w-5 h-5" /> Vehicle Photos</h2>
            <p className="text-sm text-gray-500 mb-5">Upload front and rear 3/4 photos of your vehicle showing the registration number clearly.</p>
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <FileUploadField
                    label="Front 3/4 Photo"
                    required
                    value={form.vehicle_photo_front}
                    onChange={set('vehicle_photo_front')}
                    accept=".jpg,.jpeg,.png,.heic,.heif"
                    hint="Registration number must be visible"
                  />
                  {photoPreviews.front && (
                    <img src={photoPreviews.front} alt="Front" className="mt-2 w-full h-40 object-cover rounded-lg border" />
                  )}
                </div>
                <div>
                  <FileUploadField
                    label="Rear 3/4 Photo"
                    required
                    value={form.vehicle_photo_rear}
                    onChange={set('vehicle_photo_rear')}
                    accept=".jpg,.jpeg,.png,.heic,.heif"
                    hint="Registration number must be visible"
                  />
                  {photoPreviews.rear && (
                    <img src={photoPreviews.rear} alt="Rear" className="mt-2 w-full h-40 object-cover rounded-lg border" />
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1">Back</Button>
                <Button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="pilot-submit">
                  {submitting ? 'Submitting...' : 'Submit Registration'}
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default PilotRegistration;
